import { createContext, useContext, useMemo, useState } from 'react';
import { SCHEMES, COLORS } from '../utils/colors';

/**
 * Color scheme context — lets the header toggle swap chart palettes at
 * runtime (Default / deuteranomaly-friendly / protanomaly-friendly).
 * The choice persists per browser via localStorage.
 */

const STORAGE_KEY = 'trendency-color-scheme';

const ColorSchemeContext = createContext(null);

export function ColorSchemeProvider({ children }) {
  const [schemeId, setId] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return SCHEMES[stored] ? stored : 'default';
    } catch {
      return 'default';
    }
  });

  const setSchemeId = (id) => {
    const valid = SCHEMES[id] ? id : 'default';
    setId(valid);
    try { localStorage.setItem(STORAGE_KEY, valid); } catch { /* private mode etc. */ }
  };

  const value = useMemo(() => {
    const scheme = SCHEMES[schemeId] || SCHEMES.default;
    return {
      schemeId,
      setSchemeId,
      scheme,
      palette: scheme.palette,
      distributionColors: scheme.distribution,
      // Full color object: chrome tokens + this scheme's hues
      colors: {
        ...COLORS,
        teal: scheme.teal,
        tealDark: scheme.tealDark,
        coral: scheme.coral,
        coralLight: scheme.coralLight,
        persuadable: scheme.persuadable,
        purple: scheme.purple,
        orange: scheme.orange,
        navy: scheme.navy
      }
    };
  }, [schemeId]);

  return (
    <ColorSchemeContext.Provider value={value}>
      {children}
    </ColorSchemeContext.Provider>
  );
}

export function useColorScheme() {
  const ctx = useContext(ColorSchemeContext);
  if (!ctx) throw new Error('useColorScheme must be used within ColorSchemeProvider');
  return ctx;
}
